import type { ApiToken, ApiTokenCreated, UserOrganization } from '@outpost/api-client';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Plus, Trash2, Upload } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';

import { CopyButton } from '@/components/copy-button';
import { ErrorState, ListSkeleton } from '@/components/states';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { useAuth } from '@/lib/auth';
import { relativeTime } from '@/lib/format';

/** Where the releases page links to, so the section can be scrolled into view. */
const ANCHOR = 'upload-tokens';

/**
 * The caller's own tokens for uploading source maps from CI.
 *
 * Open to every member, because a token can do no more than its creator: it
 * acts as them, only on the release upload endpoints, and only in projects they
 * can still reach. The secret is shown once, when the token is created — the
 * server keeps only a hash of it.
 */
export function UploadTokens({ organization }: { organization: UserOrganization }) {
  const { client } = useAuth();
  const queryClient = useQueryClient();
  const location = useLocation();
  const card = useRef<HTMLDivElement>(null);
  const [created, setCreated] = useState<ApiTokenCreated | null>(null);
  const [revoking, setRevoking] = useState<ApiToken | null>(null);

  const tokens = useQuery({
    queryKey: ['api-tokens', organization.id],
    queryFn: () => client.listApiTokens(organization.id),
  });
  const invalidate = () =>
    void queryClient.invalidateQueries({ queryKey: ['api-tokens', organization.id] });

  const revoke = useMutation({
    mutationFn: (tokenId: string) => client.revokeApiToken(organization.id, tokenId),
    onSuccess: (_, tokenId) => {
      // A secret on screen for a token that no longer works would only mislead.
      if (created?.api_token.id === tokenId) setCreated(null);
      setRevoking(null);
      invalidate();
    },
  });

  // The page is long, and the releases page sends people here for this card.
  useEffect(() => {
    if (location.hash === `#${ANCHOR}`) card.current?.scrollIntoView({ block: 'start' });
  }, [location.hash]);

  return (
    <Card id={ANCHOR} ref={card} className="scroll-mt-4">
      <CardHeader className="flex-row flex-wrap items-start justify-between gap-4 space-y-0">
        <div className="space-y-1.5">
          <CardTitle className="flex items-center gap-2 text-base">
            <Upload className="size-4" />
            Upload tokens
          </CardTitle>
          <CardDescription>
            For uploading source maps from CI. A token acts as you, but only to create releases and
            upload their files, in the projects of {organization.name} you can open. If you lose
            access to a project, or leave the organization, it stops working there. Only you see the
            tokens you create.
          </CardDescription>
        </div>
        <CreateTokenDialog
          organization={organization}
          onCreated={(result) => {
            setCreated(result);
            invalidate();
          }}
        />
      </CardHeader>
      <CardContent className="space-y-3">
        {created && <CreatedSecret created={created} onDismiss={() => setCreated(null)} />}

        {tokens.isLoading && <ListSkeleton rows={2} />}
        {tokens.isError && (
          <ErrorState error={tokens.error} onRetry={() => void tokens.refetch()} />
        )}
        {tokens.data?.length === 0 && (
          <p className="text-sm text-muted-foreground">You have no upload tokens here.</p>
        )}

        {tokens.data?.map((token) => (
          <TokenRow key={token.id} token={token} onRevoke={() => setRevoking(token)} />
        ))}
      </CardContent>

      <Dialog
        open={revoking !== null}
        onOpenChange={(open) => {
          if (!open) {
            setRevoking(null);
            revoke.reset();
          }
        }}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Revoke {revoking?.name}?</DialogTitle>
            <DialogDescription>
              Anything still using it, such as a CI job, can no longer upload source maps. This
              cannot be undone; create a new token to replace it.
            </DialogDescription>
          </DialogHeader>
          {revoke.error && (
            <p className="text-sm text-destructive">{(revoke.error as Error).message}</p>
          )}
          <DialogFooter>
            <Button
              variant="destructive"
              onClick={() => revoking && revoke.mutate(revoking.id)}
              disabled={revoke.isPending}
            >
              Revoke token
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Card>
  );
}

function TokenRow({ token, onRevoke }: { token: ApiToken; onRevoke: () => void }) {
  const expired = token.expires_at !== null && new Date(token.expires_at).getTime() <= Date.now();

  return (
    <div className="flex items-center gap-3 rounded-md border border-border px-3 py-2">
      <div className="min-w-0 flex-1">
        <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
          <span className="min-w-0 text-sm font-medium wrap-anywhere">{token.name}</span>
          <code className="text-xs text-muted-foreground">{token.token_prefix}…</code>
          {expired && <Badge variant="muted">expired</Badge>}
        </div>
        <span className="mt-0.5 block text-xs text-muted-foreground">
          created {relativeTime(token.created_at)} ago
          {token.last_used_at
            ? ` · last used ${relativeTime(token.last_used_at)} ago`
            : ' · never used'}
          {token.expires_at
            ? ` · ${expired ? 'expired' : 'expires'} ${new Date(token.expires_at).toLocaleDateString()}`
            : ' · never expires'}
        </span>
      </div>
      <Button variant="ghost" size="icon" title={`Revoke ${token.name}`} onClick={onRevoke}>
        <Trash2 />
        <span className="sr-only">Revoke {token.name}</span>
      </Button>
    </div>
  );
}

/** The secret of a token just created, which the server cannot show again. */
function CreatedSecret({
  created,
  onDismiss,
}: {
  created: ApiTokenCreated;
  onDismiss: () => void;
}) {
  return (
    <div className="space-y-2 rounded-md border border-primary bg-brand-subtle p-3">
      <p className="text-sm">
        Created <span className="font-medium">{created.api_token.name}</span>. Store it as the{' '}
        <code>OUTPOST_TOKEN</code> secret in your CI:
      </p>
      <div className="flex items-center gap-2">
        <Input
          readOnly
          value={created.secret}
          onFocus={(event) => event.currentTarget.select()}
          className="font-mono text-xs"
          aria-label="Upload token"
        />
        <CopyButton value={created.secret} />
      </div>
      <div className="flex flex-wrap items-center justify-between gap-2">
        <p className="text-xs text-muted-foreground">
          Copy it now: you won&apos;t see this token again. If you lose it, revoke it and create
          another.
        </p>
        <Button variant="outline" size="sm" onClick={onDismiss}>
          Done
        </Button>
      </div>
    </div>
  );
}

/** Radix needs a value for every option, and the empty string is not one. */
const NEVER = 'never';
const EXPIRY_OPTIONS = [
  { value: '30', label: '30 days' },
  { value: '90', label: '90 days' },
  { value: '365', label: '1 year' },
  { value: NEVER, label: 'Never' },
];

function CreateTokenDialog({
  organization,
  onCreated,
}: {
  organization: UserOrganization;
  onCreated: (result: ApiTokenCreated) => void;
}) {
  const { client } = useAuth();
  const [open, setOpen] = useState(false);
  const [name, setName] = useState('');
  const [expiry, setExpiry] = useState('90');

  const create = useMutation({
    mutationFn: () =>
      client.createApiToken(organization.id, {
        name: name.trim(),
        expires_in_days: expiry === NEVER ? null : Number(expiry),
      }),
    onSuccess: (result) => {
      onCreated(result);
      setOpen(false);
      setName('');
      setExpiry('90');
    },
  });

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        setOpen(next);
        if (!next) create.reset();
      }}
    >
      <DialogTrigger asChild>
        <Button variant="outline" size="sm">
          <Plus />
          Create token
        </Button>
      </DialogTrigger>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Create an upload token</DialogTitle>
          <DialogDescription>
            Name it after where it will be used, so you know what breaks if you revoke it.
          </DialogDescription>
        </DialogHeader>

        <form
          id="upload-token-form"
          className="space-y-4"
          onSubmit={(event) => {
            event.preventDefault();
            if (name.trim()) create.mutate();
          }}
        >
          <div className="space-y-1.5">
            <Label htmlFor="upload-token-name">Name</Label>
            <Input
              id="upload-token-name"
              required
              autoComplete="off"
              value={name}
              onChange={(event) => setName(event.target.value)}
              placeholder="GitHub Actions: web"
            />
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="upload-token-expiry">Expires after</Label>
            <Select value={expiry} onValueChange={setExpiry}>
              <SelectTrigger id="upload-token-expiry" className="w-40">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {EXPIRY_OPTIONS.map((option) => (
                  <SelectItem key={option.value} value={option.value}>
                    {option.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {create.error && (
            <p className="text-sm text-destructive">{(create.error as Error).message}</p>
          )}
        </form>

        <DialogFooter>
          <Button
            type="submit"
            form="upload-token-form"
            disabled={!name.trim() || create.isPending}
          >
            Create token
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
